import React from 'react'
import { connect } from '@cerebral/react'
import { sequences, state } from 'cerebral'
import { Box, Button, Link, Snackbar } from '@mui/material'
import { Check, Visibility } from '@mui/icons-material'
import { capitalize } from 'underscore.string'
import format from '../../../shared/format'
import { expandEntities } from '../../modules/computed'
import { routePaths } from '../../modules/route'
import ServiceDismissModal from '../views/service-requests/ServiceDismissModal'
import { useToggleState } from '../../lib/util/hooks'
import { ServiceRequest } from '../../types/ServiceRequest'
import { Birthday } from '../../types/Profile'

interface RoutePaths {
  toPerson: Function
}

const { toPerson } = routePaths as RoutePaths

interface ConnectedProps {
  birthdays: Birthday[]
  pendingServiceRequests: ServiceRequest[]
  acknowledgeServiceRequest: ({ id }: { id: string }) => void
  acknowledgeBirthday: (payload: { id: string }) => void
  setUpdateForBirthday: (payload: { profileId: string }) => void
  requestingRoleIds: string[]
  updateUserTypes: (payload: { id: string; add: string[] }) => void
  get: (path: string) => any
  updatesEnabled: boolean
}

const CurrentSnackbars = connect(
  {
    birthdays: state`profiles.todaysBirthdays`,
    pendingServiceRequests: state`serviceRequests.pendingRequests`,
    acknowledgeBirthday: sequences`profiles.acknowledgeBirthday`,
    setUpdateForBirthday: sequences`updates.setUpdateForBirthday`,
    requestingRoleIds: state`profiles.requestingRoleIds`, // This dependency is here to ensure the component redraws.
    updateUserTypes: sequences`profiles.updateUserTypes`,
    acknowledgeServiceRequest: sequences`serviceRequests.acknowledgeServiceRequest`,
    updatesEnabled: state`account.packages.updates`,
  },

  ({
    birthdays = [],
    pendingServiceRequests = [],
    acknowledgeBirthday,
    setUpdateForBirthday,
    updateUserTypes,
    get,
    acknowledgeServiceRequest,
    updatesEnabled,
  }: ConnectedProps) => {
    const [modalVisible, openModal, closeModal] = useToggleState(false)

    const profilesRequestingRole = expandEntities('profiles', state`profiles.requestingRoleIds`)(get)
    let index = 0

    return (
      <>
        {birthdays.map(({ name, _id: id }: any = {}) => {
          return (
            <Snackbar
              key={id}
              style={{ marginBottom: index++ * 60 }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              open={true}
              message={`Today is ${name?.first || name?.display}'s birthday`}
              action={
                <Box style={{ display: 'flex', flexDirection: 'row' }}>
                  <Box mr={2} style={{ flex: 1 }}>
                    <Button color="primary" onClick={() => acknowledgeBirthday({ id })} size="small">
                      Dismiss
                    </Button>
                  </Box>
                  {Boolean(updatesEnabled) && (
                    <Button color="primary" onClick={() => setUpdateForBirthday({ profileId: id })} size="small">
                      Announce on digital signage
                    </Button>
                  )}
                </Box>
              }
            />
          )
        })}
        {profilesRequestingRole.map(({ name, userTypes = [], requestedUserTypes = [], _id: id }: any = {}) => {
          return requestedUserTypes.length === 0 ? null : (
            <Snackbar
              key={id}
              style={{ marginBottom: index++ * 60 }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              open={true}
              message={`Role request from ${format.name(name)}`}
              action={
                <Box style={{ display: 'flex', flexDirection: 'row' }}>
                  {requestedUserTypes.map((role = '') => (
                    <Box key={role} mr={2}>
                      <Button color="primary" onClick={() => updateUserTypes({ id, add: [role] })} size="small" startIcon={<Check />}>
                        Grant {capitalize(role)} Role
                      </Button>
                    </Box>
                  ))}
                  <Button color="primary" href={toPerson({ id })} component={Link} size="small" startIcon={<Visibility />}>
                    Review
                  </Button>
                </Box>
              }
            />
          )
        })}

        {pendingServiceRequests.map(({ _id: requestId, profile: profileId, type }: ServiceRequest) => {
          const profile = profileId && get(state`profiles.entities.${profileId}`)
          const formattedName = profile && format.name(profile?.name)
          return (
            <React.Fragment key={requestId}>
              <ServiceDismissModal visible={modalVisible} id={requestId} onClose={closeModal} />
              <Snackbar
                style={{ marginBottom: index++ * 60 }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                open={true}
                message={type ? `${formattedName} is requesting "${type}"` : `${formattedName} has a service request.`}
                action={
                  <Box style={{ display: 'flex', flexDirection: 'row' }}>
                    <Box mr={2} style={{ flex: 1 }}>
                      <Button color="primary" onClick={openModal} size="small">
                        Dismiss
                      </Button>
                    </Box>
                    <Button color="primary" onClick={() => acknowledgeServiceRequest({ id: requestId })} size="small">
                      Acknowledge
                    </Button>
                  </Box>
                }
              />
            </React.Fragment>
          )
        })}
      </>
    )
  }
)

export default CurrentSnackbars
